/* ---------------------------------------------------------------------------
   The periods a construction report is written for.

   Proposal 1 §4 promises FWO weekly, monthly and quarterly reports. This is the
   arithmetic behind those three words, in one place because two screens and a
   print layout all have to agree about which days "the week of the 8th" means.

   **PURE, AND TAKING TODAY AS AN ARGUMENT.** Nothing here reads the clock. A
   period that depended on `new Date()` inside the function could not be checked
   without either mocking time or asserting against whatever day the suite
   happened to run on, and this is arithmetic with four off-by-one traps in it.

   **BOTH ENDS INCLUSIVE.** `from` and `to` are days the report covers, not a
   half-open range — which is how somebody asks for "the 8th to the 14th", how
   the endpoint queries (`occurred_on >= ?2 AND occurred_on <= ?3`), and how the
   heading prints. A half-open range would silently drop everything recorded on
   the last day of every report.

   **THE WEEK STARTS ON MONDAY.** ISO-8601, and it is a real choice rather than
   a default: a construction week is a working week, the monthly report is
   assembled from the weekly ones, and a Sunday start would put two calendar
   months in the same first week more often than not. Where a department works a
   Saturday-to-Friday week — which is common in Pakistan — the custom range is
   the answer, and it is offered on the screen beside the three presets.

   **DATES ARE UTC THROUGHOUT.** `occurred_on` is a calendar date with no time
   and no zone: the day somebody says the pour happened. Building these
   boundaries in local time would make the report boundary move with the
   reader's device — a Kathmandu reader and a Bangkok reader would get different
   weeks out of the same register — so every date here is constructed and read
   in UTC, and the only thing that crosses the boundary is a `YYYY-MM-DD`
   string.
   --------------------------------------------------------------------------- */

/** The three cadences §4 promises, plus the range somebody typed themselves. */
export type PeriodKind = 'week' | 'month' | 'quarter' | 'custom';

export const PERIOD_KINDS: readonly PeriodKind[] = ['week', 'month', 'quarter', 'custom'];

export function isPeriodKind(value: unknown): value is PeriodKind {
  return typeof value === 'string' && (PERIOD_KINDS as readonly string[]).includes(value);
}

/** Inclusive at both ends. `YYYY-MM-DD`. */
export interface Period {
  from: string;
  to: string;
}

/** `YYYY-MM-DD`, which is the only date shape this register stores or accepts. */
export const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDay(value: string): boolean {
  if (!ISO_DAY.test(value)) return false;
  /* The shape is not enough: `2026-02-31` matches it. Round-tripping through
     Date is what catches a day that does not exist, and it has to be compared
     back as a string because Date will happily roll it into March. */
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function utc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/** Days added to a calendar date, in UTC. Negative moves back. */
export function addDays(iso: string, days: number): string {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
}

/**
 * The period of the given kind that contains `today`.
 *
 * `custom` returns the single day, which is the honest answer to "what range
 * does a custom period cover before anybody has typed one" — the caller
 * replaces both ends. It is not an error and does not throw: a screen asking
 * for it while the user is mid-edit is ordinary.
 */
export function periodFor(kind: PeriodKind, today: string): Period {
  if (!isIsoDay(today)) return { from: today, to: today };

  if (kind === 'week') {
    const d = utc(today);
    /* getUTCDay is 0 for Sunday, so Sunday is six days after the Monday that
       starts its week rather than the day before the one that follows it —
       the classic ISO off-by-one, written out rather than left to a reader. */
    const dow = d.getUTCDay();
    const back = dow === 0 ? 6 : dow - 1;
    const from = addDays(today, -back);
    return { from, to: addDays(from, 6) };
  }

  if (kind === 'month') {
    const d = utc(today);
    const from = `${today.slice(0, 7)}-01`;
    /* Day 0 of the NEXT month is the last day of this one, which is how this
       avoids a table of month lengths and the leap-year rule with it. */
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
    return { from, to: toIso(last) };
  }

  if (kind === 'quarter') {
    const d = utc(today);
    const q = Math.floor(d.getUTCMonth() / 3);
    const from = new Date(Date.UTC(d.getUTCFullYear(), q * 3, 1));
    const to = new Date(Date.UTC(d.getUTCFullYear(), q * 3 + 3, 0));
    return { from: toIso(from), to: toIso(to) };
  }

  return { from: today, to: today };
}

/** The same period, one step back. What a "previous" control moves through. */
export function previousPeriod(kind: PeriodKind, p: Period): Period {
  if (kind === 'week') return periodFor('week', addDays(p.from, -7));
  if (kind === 'month') return periodFor('month', addDays(p.from, -1));
  if (kind === 'quarter') return periodFor('quarter', addDays(p.from, -1));
  /* A custom range moves by its own length, which is the only meaning a "back"
     control can have for a range somebody chose the ends of. */
  const span = daysBetween(p.from, p.to);
  return { from: addDays(p.from, -(span + 1)), to: addDays(p.to, -(span + 1)) };
}

/** The same period, one step forward. */
export function nextPeriod(kind: PeriodKind, p: Period): Period {
  if (kind === 'week') return periodFor('week', addDays(p.to, 1));
  if (kind === 'month') return periodFor('month', addDays(p.to, 1));
  if (kind === 'quarter') return periodFor('quarter', addDays(p.to, 1));
  const span = daysBetween(p.from, p.to);
  return { from: addDays(p.from, span + 1), to: addDays(p.to, span + 1) };
}

/** Whole days from one date to another. Zero on the same day, never negative. */
export function daysBetween(from: string, to: string): number {
  const a = utc(from).getTime();
  const b = utc(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
  return Math.round((b - a) / 86_400_000);
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * What to call the period at the top of the report.
 *
 * NAMED RATHER THAN DATED WHERE IT HAS A NAME — "September 2026", "Q3 2026" —
 * because that is what the covering email will call it and the two should
 * match. A week has no name, so it prints its days; a custom range prints its
 * days too, and neither is ever printed as a bare number of days.
 *
 * The exact dates are printed underneath by the screen regardless. This is the
 * title, not the definition.
 */
export function periodTitle(kind: PeriodKind, p: Period): string {
  if (kind === 'month' && p.from.endsWith('-01')) {
    const month = MONTHS[Number(p.from.slice(5, 7)) - 1];
    if (month) return `${month} ${p.from.slice(0, 4)}`;
  }
  if (kind === 'quarter' && p.from.endsWith('-01')) {
    const q = Math.floor((Number(p.from.slice(5, 7)) - 1) / 3) + 1;
    return `Q${q} ${p.from.slice(0, 4)}`;
  }
  return `${p.from} to ${p.to}`;
}

/** What the cadence is called, for a heading that has to say which report this is. */
export const PERIOD_LABEL: Record<PeriodKind, string> = {
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
  custom: 'Period',
};
